
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload, Trash2 } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { useState } from "react";
import { useCompanyLogo } from "./hooks/useCompanyLogo";

interface LogoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  companyId?: string;
  currentLogoUrl: string;
  onLogoUpdate: (newLogoUrl: string | null) => void;
}

export function LogoDialog({
  isOpen,
  onClose,
  companyId,
  currentLogoUrl,
  onLogoUpdate,
}: LogoDialogProps) {
  const [logoUrlInput, setLogoUrlInput] = useState(currentLogoUrl || '');
  
  const handleSuccess = (newUrl: string | null) => {
    onClose();
    onLogoUpdate(newUrl);
  };

  const { isUploading, handleFileUpload, handleLogoUrlUpdate, handleLogoRemove } = useCompanyLogo({
    companyId: companyId || '',
    onComplete: handleSuccess
  });

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const success = await handleFileUpload(file);
      // Close dialog on failed upload
      if (!success) onClose();
    }
  };

  const handleUrlSubmit = async () => {
    const success = await handleLogoUrlUpdate(logoUrlInput);
    // Close dialog on failed upload
    if (!success) onClose();
  };

  const handleRemove = async () => {
    const success = await handleLogoRemove();
    // Close dialog on failed removal
    if (!success) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Update Company Logo</DialogTitle>
          <DialogDescription>
            Upload a new logo for your company or provide a logo URL.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Logo URL</label>
            <Input
              type="text"
              placeholder="Enter logo URL"
              value={logoUrlInput}
              onChange={(e) => setLogoUrlInput(e.target.value)}
              disabled={isUploading}
            />
            <Button 
              onClick={handleUrlSubmit}
              disabled={isUploading || !logoUrlInput.trim()}
              className="w-full"
            >
              {isUploading ? (
                <>
                  <Spinner size="sm" className="mr-2" ariaLabel="Updating" />
                  Updating...
                </>
              ) : (
                "Update URL"
              )}
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <input
              type="file"
              accept="image/*"
              id="logo-upload"
              onChange={handleFileChange}
              disabled={isUploading}
              className="hidden"
            />
            <label htmlFor="logo-upload">
              <Button 
                variant="outline" 
                className="w-full cursor-pointer" 
                disabled={isUploading}
                asChild
              >
                <div>
                  {isUploading ? (
                    <>
                      <Spinner size="sm" className="mr-2" ariaLabel="Uploading" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload Image
                    </>
                  )}
                </div>
              </Button>
            </label>
          </div>

          {currentLogoUrl && (
            <Button
              variant="destructive"
              onClick={handleRemove}
              disabled={isUploading}
              className="w-full"
            >
              {isUploading ? (
                <>
                  <Spinner size="sm" className="mr-2" ariaLabel="Removing" />
                  Removing...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove Logo
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
